import {useState, useEffect} from "react";
import {Dialog, DialogTitle, DialogContent, DialogActions, Box} from "@mui/material";
import {ArrowLeft, Plus, Edit, Trash2, MessageCircle} from "lucide-react";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import SoftBox from "../../../components/SoftBox";
import SoftButton from "../../../components/SoftButton";
import SoftTypography from "../../../components/SoftTypography";
import SoftInput from "../../../components/SoftInput";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import PropTypes from "prop-types";
import {addQuestion, deleteQuestion, getQuestionsByCategory, updateQuestion} from "../../../axios-client";

const QuestionManager = ({category, language, onBack, fetchList, setNotification}) => {
    const [questions, setQuestions] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [deleteQuestionId, setDeleteQuestionId] = useState(null);

    const categoryName = language === "en" ? category.name.en : category.name.ar;

    useEffect(() => {
        fetchQuestions();
    }, [category.id]);

    const fetchQuestions = async () => {
        try {
            const res = await getQuestionsByCategory(category.id);
            setQuestions(res.data);
        } catch (error) {
            console.error("Failed to fetch questions:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        try {
            await deleteQuestion(questionId);
            setQuestions(prev => prev.filter(q => q.id !== questionId));
            fetchList()
        } catch (error) {
            console.error("Failed to delete question:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };

    const handleSaveQuestion = async (questionData) => {
        try {
            if (editingQuestion) {
                const res = await updateQuestion({
                    id: editingQuestion.id,
                    ...questionData
                });
                setQuestions(prev =>
                    prev.map(q => q.id === editingQuestion.id ? res.data : q)
                );
                setEditingQuestion(null);
            } else {
                const res = await addQuestion(category.id, questionData);
                setQuestions(prev => [...prev, res.data]);
                setIsAddModalOpen(false);
            }
            fetchList()
        } catch (error) {
            console.error("Failed to save question:", error);
            setNotification({
                show: true,
                snackbarMessage: 'An error occurred. Please try again.',
                severity: 'error',
            });
        }
    };

    return (
        <SoftBox display="flex" flexDirection="column" gap={4}>
            {/* Header */}
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                <SoftBox display="flex" alignItems="center" gap={2}>
                    <SoftButton variant="outlined" onClick={onBack} startIcon={<ArrowLeft size={16}/>}>
                        Back to Categories
                    </SoftButton>
                    <SoftBox>
                        <SoftTypography variant="h5" fontWeight="bold">{categoryName}</SoftTypography>
                        <SoftTypography variant="body2" color="text">Manage questions for this category</SoftTypography>
                    </SoftBox>
                </SoftBox>

                <SoftButton variant="contained" color="primary" onClick={() => setIsAddModalOpen(true)}
                            startIcon={<Plus size={16}/>}>
                    Add Question
                </SoftButton>
            </SoftBox>

            {/* Questions List */}
            <SoftBox display="grid" gap={3}>
                {questions.map((q) => {
                    const questionText = language === "en" ? q.question.en : q.question.ar;

                    return (
                        <Card key={q.id}>
                            <CardContent>
                                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                                    <SoftBox display="flex" alignItems="start" gap={2} flex="1">
                                        <SoftBox width={32} height={32} borderRadius="lg" bgcolor="primary.lighter"
                                                 display="flex" alignItems="center" justifyContent="center" mt={1.5}>
                                            <MessageCircle size={16} color="#3b82f6"/>
                                        </SoftBox>
                                        <SoftBox flex="1">
                                            <SoftTypography fontSize="1rem" fontWeight="medium"
                                                            mt={1.5}>{questionText}</SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox display="flex" gap={1} mt={1.5}>
                                        <SoftButton variant="text" sx={{minWidth: "auto", padding: 1}}
                                                    onClick={() => setEditingQuestion(q)}>
                                            <Edit size={16} sx={{p: 0.5}}/>
                                        </SoftButton>
                                        <SoftButton variant="text" sx={{minWidth: "auto", padding: 1}}
                                                    onClick={() => setDeleteQuestionId(q.id)} color="error">
                                            <Trash2 size={16} sx={{p: 0.5}}/>
                                        </SoftButton>
                                    </SoftBox>
                                </SoftBox>
                            </CardContent>
                        </Card>
                    );
                })}

                {questions.length === 0 && (
                    <Card>
                        <CardContent>
                            <SoftBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                                <MessageCircle size={48} color="#9ca3af"/>
                                <SoftTypography variant="h6" color="text">No Questions Yet</SoftTypography>
                                <SoftTypography variant="body2" color="text">Add your first question to get
                                    started.</SoftTypography>
                            </SoftBox>
                        </CardContent>
                    </Card>
                )}
            </SoftBox>

            {/* Add/Edit Question Modal */}
            <QuestionModal
                isOpen={isAddModalOpen || !!editingQuestion}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setEditingQuestion(null);
                }}
                question={editingQuestion}
                onSave={handleSaveQuestion}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDeleteDialog
                isOpen={!!deleteQuestionId}
                onClose={() => setDeleteQuestionId(null)}
                onConfirm={() => {
                    if (deleteQuestionId) {
                        handleDeleteQuestion(deleteQuestionId);
                        setDeleteQuestionId(null);
                    }
                }}
                title="Delete Question"
                description="Are you sure you want to delete this question? This action cannot be undone."
                itemName={deleteQuestionId ? questions.find(q => q.id === deleteQuestionId)?.question[language] : undefined}
            />
        </SoftBox>
    );
};

const QuestionModal = ({isOpen, onClose, question, onSave}) => {
    const [formData, setFormData] = useState({question: {en: "", ar: ""}});

    useEffect(() => {
        if (question) {
            setFormData({question: question.question});
        } else {
            setFormData({question: {en: "", ar: ""}});
        }
    }, [question, isOpen]);

    const handleSave = () => {
        if (formData.question.en.trim() && formData.question.ar.trim()) {
            onSave(formData);
            setFormData({question: {en: "", ar: ""}});
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose}
                maxWidth="md" // options: 'xs', 'sm', 'md', 'lg', 'xl', false
                sx={{"& .MuiPaper-root": {width: "40%"}}}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={2}>
                    <SoftTypography variant="h6" fontWeight="bold">
                        {question ? "Edit Question" : "Add New Question"}
                    </SoftTypography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <SoftBox borderRadius="xl" display="flex" flexDirection="column" gap={3} boxShadow="soft"
                         bgcolor="white">
                    {/* Question fields */}
                    <SoftBox display="flex" flexDirection="column" gap={2}>
                        <SoftTypography variant="body2" fontWeight="medium">Question</SoftTypography>
                        <SoftInput
                            value={formData.question.en}
                            onChange={e => setFormData(prev => ({
                                ...prev,
                                question: {...prev.question, en: e.target.value}
                            }))}
                            placeholder="Enter question in English"
                        />
                        <SoftInput
                            value={formData.question.ar}
                            onChange={e => setFormData(prev => ({
                                ...prev,
                                question: {...prev.question, ar: e.target.value}
                            }))}
                            placeholder="أدخل السؤال بالعربية"
                            dir="rtl"
                        />
                    </SoftBox>
                </SoftBox>
            </DialogContent>

            <DialogActions>
                <SoftButton variant="outlined" color="secondary" onClick={onClose}>Cancel</SoftButton>
                <SoftButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    autoFocus
                    disabled={
                        !formData.question.en.trim() ||
                        !formData.question.ar.trim()
                    }
                >
                    {question ? "Update" : "Create"}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

QuestionManager.propTypes = {
    category: PropTypes.object,
    language: PropTypes.string,
    onBack: PropTypes.func,
    fetchList: PropTypes.func,
    setNotification: PropTypes.func,
};

QuestionModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    question: PropTypes.object,
};


export default QuestionManager;
